import { ApolloLink, execute, GraphQLRequest, makePromise } from 'apollo-link';
import { BatchHttpLink } from 'apollo-link-batch-http';
import axios from 'axios';

const axiosFetch = async (uri: string, options: RequestInit) => {
  // BatchHttpLink passes body as an array of GraphQLRequest
  const requests = JSON.parse(options.body as string) as GraphQLRequest[];

  // Embed operation identifier into uri for New Relic AJAX tracing
  const baseUri = uri.endsWith('/') ? uri : `${uri}/`;
  const operationName = encodeURIComponent(requests[0].operationName || 'anonymous');
  const operationPath = requests.length > 1 ? `${operationName}/batch/${requests.length}` : `${operationName}`;

  const resp = await axios.request({
    url: baseUri + operationPath,
    method: options.method,
    headers: options.headers,
    data: options.body,
    withCredentials: true,
  });
  return new Response(JSON.stringify(resp.data), {
    status: resp.status,
    statusText: resp.statusText,
    headers: new Headers(resp.headers),
  });
};

class GraphQLClient {
  private readonly httpLink: ApolloLink;

  constructor({ endpointUrl }: { endpointUrl: string }) {
    this.httpLink = new BatchHttpLink({ uri: endpointUrl, fetch: axiosFetch, batchKey: op => op.operationName });
  }

  query = async <ResultType = Record<string, any>, VariablesType = Record<string, any>>(
    query: any,
    variables: VariablesType = {} as any,
  ): Promise<ResultType> => {
    try {
      const result = await makePromise(
        execute(this.httpLink, {
          query,
          variables,
        }),
      );

      if (result.errors) {
        throw new GraphQLOperationFailed({ type: (result.errors[0] as any).type, query, variables });
      }

      return result.data as ResultType;
    } catch (err) {
      if (/Response not successful: Received status code [0-9]{3}/.test(err.message)) {
        throw new GraphQLHttpError({ query, variables, response: err.response });
      }

      throw err;
    }
  };
}

export class GraphQLQueryException extends Error {
  public name = 'GraphQLQueryException';
  public readonly message: string;
  public readonly query: any;
  public readonly variables: Record<string, any>;

  constructor({ message, query, variables }: { message: string; query: any; variables: Record<string, any> }) {
    super();

    this.message = message;
    this.query = query;
    this.variables = variables;
  }
}

export class GraphQLHttpError extends GraphQLQueryException {
  public readonly name = 'GraphQLHttpError';
  public readonly response: Response;

  constructor({ query, variables, response }: { query: any; variables: Record<string, any>; response: Response }) {
    super({
      message: `A GraphQL operation is failed because the HTTP response has an exceptional status code. (${
        response.status
      } ${response.statusText})`,
      query,
      variables,
    });

    this.response = response;
  }
}

export class GraphQLOperationFailed extends GraphQLQueryException {
  public readonly name = 'GraphQLOperationFailed';
  public readonly type: string;

  constructor({ type, query, variables }: { type: string; query: any; variables: Record<string, any> }) {
    super({
      message: `A GraphQL operation is failed because the request is incorrect. (type: ${type})`,
      query,
      variables,
    });

    this.type = type;
  }
}

export default GraphQLClient;
